import {
  AdminSpinner,
  Button,
  Disclaimer,
  Div,
  ErrorToast,
  H5,
  ImagePlaceholderIcon,
  ImagePreview,
  Modal,
  P,
  businessLicenceDummy,
  lightTheme,
} from '@vgrubs/components';
import Compressor from 'compressorjs';
import { ChangeEvent, useRef } from 'react';
import { useLocationBusinessLicense } from './hooks/useLocationBusinessLicense';

/** Same ceiling every other image upload in the app compresses down to. */
const MAX_SIZE = 2097152;

type Props = {
  /** The location picked in Settings. The modal is open while it is set. */
  locationId: number | null;
  closeModal: () => void;
};

/**
 * A complete location's business license, from Settings. It can only be
 * replaced: the license is required, so there is no delete. Locations made
 * before it became required may have none and get an empty state instead.
 */
export const BusinessLicenseModal = ({ locationId, closeModal }: Props) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const { license, isLoading, isError, replace, replacing } =
    useLocationBusinessLicense(locationId);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Cleared so picking the same file again after a failure still fires.
    e.target.value = '';
    if (!file) return;

    new Compressor(file, {
      quality: 0.8,
      maxWidth: 2500,
      maxHeight: 1500,
      convertSize: MAX_SIZE,
      success: (result) => {
        if (result.size > MAX_SIZE) return ErrorToast('Image file too big');
        replace(result);
      },
      // Only images are accepted; the API answers 415 to anything else.
      error: () => ErrorToast('Please choose an image'),
    });
  };

  return (
    <>
      {locationId != null && (isLoading || replacing) && <AdminSpinner />}

      <Modal
        isOpen={locationId != null}
        closeModal={closeModal}
        logo={false}
        header="Business License"
        footer={
          <Div $flex $column $alignCenter $gap="16px" $width="100%">
            <Button
              $selected
              $w="100%"
              disabled={isLoading || replacing}
              onClick={() => fileRef.current?.click()}
            >
              {license ? 'Upload New License' : 'Upload Business License'}
            </Button>
            <ImagePreview
              image={businessLicenceDummy}
              lightbox
              ToggleComponent={(onClick) => (
                <H5 $mb="0" $pointer onClick={onClick}>
                  <span>See Example</span>
                </H5>
              )}
            />
          </Div>
        }
      >
        {license ? (
          <>
            <ImagePreview
              image={license}
              lightbox
              borderRadius="16px"
              style={{ aspectRatio: '3 / 2', objectFit: 'cover' }}
            />
            <P $m="16px 0 0" $center $color={lightTheme.darkGray}>
              A new image replaces the current license. It cannot be removed,
              only replaced.
            </P>
          </>
        ) : (
          !isLoading && (
            <Div $m="24px 0" $flex $column $alignCenter $gap="8px">
              <ImagePlaceholderIcon />
              <Disclaimer $center $m="0">
                {isError
                  ? 'Could not load the current license.'
                  : 'No business license uploaded'}
              </Disclaimer>
            </Div>
          )
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFile}
        />
      </Modal>
    </>
  );
};
